import { useMemo } from "react";

export const useMatrixData = ({ strikes, expectedMove }) => {
  // Comprehensive Matrix View - All Strikes x All Days
  const comprehensiveMatrixData = useMemo(() => {
    if (!strikes.length) return [];

    return strikes.map((strikeData) => {
      const rowData = {
        strike: strikeData.strike,
        iv: strikeData.iv,
        bid: strikeData.bid,
        ask: strikeData.ask,
        last: strikeData.last,
      };

      strikeData.trading_days.forEach((td) => {
        const scenario = td.scenarios[`${expectedMove}%`];
        if (scenario) {
          rowData[`day_${td.trading_days_passed}`] = {
            returnPct: scenario.price_change_pct,
            profit: scenario.price_change,
            entryPrice: td.theoretical_price_now,
            exitPrice: scenario.theoretical_price_future,
            daysToExpiration: td.days_to_expiration,
            isProfit: scenario.price_change > 0,
            riskLevel:
              scenario.price_change_pct < -50
                ? "critical"
                : scenario.price_change_pct < -25
                  ? "high"
                  : scenario.price_change_pct < 0
                    ? "medium"
                    : "safe",
          };
        }
      });

      return rowData;
    });
  }, [strikes, expectedMove]);

  // Calculate min/max values for dynamic scaling
  const heatmapScale = useMemo(() => {
    if (!comprehensiveMatrixData.length)
      return { min: 0, max: 100, maxPositive: 100, minNegative: 0 };

    let maxPositive = 0;
    let minNegative = 0;

    comprehensiveMatrixData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key.startsWith("day_")) {
          const data = row[key];
          if (data && data.returnPct !== undefined) {
            if (data.returnPct > maxPositive) maxPositive = data.returnPct;
            if (data.returnPct < minNegative) minNegative = data.returnPct;
          }
        }
      });
    });

    return {
      maxPositive: Math.max(maxPositive, 10),
      minNegative: Math.min(minNegative, -10),
    };
  }, [comprehensiveMatrixData]);

  // Get all unique trading days for matrix columns
  const allTradingDays = useMemo(() => {
    if (!strikes.length || !strikes[0].trading_days) return [];
    return strikes[0].trading_days
      .map((td) => td.trading_days_passed)
      .sort((a, b) => a - b);
  }, [strikes]);

  // Multi-Strike Comparison - Show all strikes on same chart
  const multiStrikeTimeDecayData = useMemo(() => {
    if (!strikes.length) return [];

    const days =
      strikes[0]?.trading_days.map((td) => td.trading_days_passed) || [];

    return days.map((day) => {
      const dataPoint = { day };

      strikes.forEach((strikeData) => {
        const td = strikeData.trading_days.find(
          (t) => t.trading_days_passed === day,
        );
        const scenario = td?.scenarios[`${expectedMove}%`];
        if (scenario) {
          dataPoint[`Strike $${strikeData.strike}`] = scenario.price_change_pct;
          dataPoint[`Strike $${strikeData.strike}_profit`] =
            scenario.price_change;
        }
      });

      return dataPoint;
    });
  }, [strikes, expectedMove]);

  return {
    comprehensiveMatrixData,
    heatmapScale,
    allTradingDays,
    multiStrikeTimeDecayData,
  };
};
